/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Recent Developer API Logs Table (Task 10.6.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  ListFilter,
  Search,
  Download,
  Copy,
  Check,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock,
  Key,
} from 'lucide-react';
import { useTranslation } from '../../../i18n';
import {
  useDeveloperAnalyticsStore,
  StatusFilter,
} from '../../../stores/useDeveloperAnalyticsStore';
import { useApiKeyStore } from '../../../stores/useApiKeyStore';

export const RecentApiLogsTable: React.FC = () => {
  const { t } = useTranslation();
  const { keys } = useApiKeyStore();
  const {
    selectedKeyId,
    statusFilter,
    searchQuery,
    setSelectedKeyId,
    setStatusFilter,
    setSearchQuery,
    getFilteredLogs,
    exportLogsAsCsv,
  } = useDeveloperAnalyticsStore();

  const [copiedId, setCopiedId] = useState<string | null>(null);

  const logs = getFilteredLogs();

  const handleCopy = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // Fallback
    }
  };

  const handleDownloadCsv = () => {
    const csvContent = exportLogsAsCsv();
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `sophia-api-logs-${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatTimestamp = (timestamp: number) => {
    const d = new Date(timestamp);
    const h = d.getHours().toString().padStart(2, '0');
    const m = d.getMinutes().toString().padStart(2, '0');
    const s = d.getSeconds().toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
  };

  const formatRelativeTime = (timestamp: number) => {
    const diffSec = Math.floor((Date.now() - timestamp) / 1000);
    if (diffSec < 60) return `${diffSec}s ago`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    return `${diffHr}h ago`;
  };

  return (
    <div className="p-5 rounded-2xl border border-surface-border bg-surface space-y-4">
      {/* Table Header and Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="space-y-0.5">
          <div className="flex items-center gap-2">
            <ListFilter className="size-4 text-primary" />
            <h3 className="text-sm font-bold font-heading text-foreground">
              {t('apikeys.logsTitle')}
            </h3>
            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-primary/10 text-primary">
              {logs.length} / 50
            </span>
          </div>
          <p className="text-xs text-foreground-muted">
            {t('apikeys.logsSubtitle')}
          </p>
        </div>

        {/* Export CSV Button */}
        <button
          onClick={handleDownloadCsv}
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-surface-border bg-surface-elevated hover:bg-surface-subtle text-xs font-semibold text-foreground transition-all shadow-xs active:scale-95 shrink-0 self-start sm:self-auto"
        >
          <Download className="size-3.5 text-primary" />
          <span>{t('apikeys.exportCsv')}</span>
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 pt-1">
        {/* Search Input */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-foreground-muted" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('apikeys.searchLogsPlaceholder')}
            className="w-full pl-9 pr-4 py-2 rounded-xl bg-surface-elevated border border-surface-border text-xs text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all"
          />
        </div>

        {/* Filters Group */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Key Selector */}
          <select
            value={selectedKeyId}
            onChange={(e) => setSelectedKeyId(e.target.value)}
            className="px-3 py-2 rounded-xl bg-surface-elevated border border-surface-border text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all"
          >
            <option value="all">{t('apikeys.filterAllKeys')}</option>
            {keys.map((k) => (
              <option key={k.id} value={k.id}>
                {k.name} (••••{k.keyHint})
              </option>
            ))}
          </select>

          {/* Status Filter Pills */}
          <div className="p-0.5 rounded-xl bg-surface-elevated border border-surface-border flex items-center gap-0.5">
            {(
              [
                { id: 'all', label: t('apikeys.statusAll') },
                { id: '2xx', label: t('apikeys.status2xx') },
                { id: '4xx', label: t('apikeys.status4xx') },
                { id: '5xx', label: t('apikeys.status5xx') },
              ] as const
            ).map((st) => (
              <button
                key={st.id}
                onClick={() => setStatusFilter(st.id as StatusFilter)}
                className={`px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all ${
                  statusFilter === st.id
                    ? 'bg-primary text-primary-fg font-bold shadow-xs'
                    : 'text-foreground-muted hover:text-foreground'
                }`}
              >
                {st.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table Display */}
      <div className="overflow-x-auto rounded-xl border border-surface-border">
        <table className="w-full text-left border-collapse text-xs">
          <thead>
            <tr className="border-b border-surface-border bg-surface-subtle text-[11px] font-semibold text-foreground-muted uppercase tracking-wider">
              <th className="py-3 px-4">{t('apikeys.colTimestamp')}</th>
              <th className="py-3 px-3">{t('apikeys.colMethod')}</th>
              <th className="py-3 px-4">{t('apikeys.colEndpoint')}</th>
              <th className="py-3 px-3">{t('apikeys.colStatus')}</th>
              <th className="py-3 px-3">{t('apikeys.colLatency')}</th>
              <th className="py-3 px-3">{t('apikeys.colTokens')}</th>
              <th className="py-3 px-3">{t('apikeys.colKey')}</th>
              <th className="py-3 px-4">{t('apikeys.colIp')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-border">
            {logs.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-12 text-center text-foreground-muted">
                  <p className="text-xs">{t('apikeys.emptyLogs')}</p>
                </td>
              </tr>
            ) : (
              logs.map((log) => {
                const is200 = log.statusCode >= 200 && log.statusCode < 300;
                const is429 = log.statusCode === 429;

                return (
                  <tr
                    key={log.id}
                    className="hover:bg-surface-subtle/50 transition-colors group"
                  >
                    {/* Timestamp */}
                    <td className="py-3 px-4 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <Clock className="size-3 text-foreground-muted shrink-0" />
                        <span className="font-mono text-foreground font-medium">
                          {formatTimestamp(log.timestamp)}
                        </span>
                        <span className="text-[10px] text-foreground-muted">
                          ({formatRelativeTime(log.timestamp)})
                        </span>
                      </div>
                    </td>

                    {/* Method */}
                    <td className="py-3 px-3 whitespace-nowrap">
                      <span
                        className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded ${
                          log.method === 'POST'
                            ? 'bg-primary/10 text-primary border border-primary/20'
                            : 'bg-accent/15 text-accent border border-accent/20'
                        }`}
                      >
                        {log.method}
                      </span>
                    </td>

                    {/* Endpoint */}
                    <td className="py-3 px-4 whitespace-nowrap">
                      <code className="text-xs font-mono text-foreground font-medium">
                        {log.endpoint}
                      </code>
                    </td>

                    {/* Status Code */}
                    <td className="py-3 px-3 whitespace-nowrap">
                      {is200 ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-mono font-bold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                          <CheckCircle2 className="size-3" />
                          {log.statusCode}
                        </span>
                      ) : is429 ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-mono font-bold px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-500 border border-amber-500/20">
                          <AlertTriangle className="size-3" />
                          {log.statusCode}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-mono font-bold px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-500 border border-rose-500/20">
                          <XCircle className="size-3" />
                          {log.statusCode}
                        </span>
                      )}
                    </td>

                    {/* Latency */}
                    <td className="py-3 px-3 whitespace-nowrap">
                      <span
                        className={`text-[11px] font-mono font-semibold ${
                          log.latencyMs < 100
                            ? 'text-emerald-500'
                            : log.latencyMs < 300
                            ? 'text-amber-500'
                            : 'text-rose-500'
                        }`}
                      >
                        {log.latencyMs}ms
                      </span>
                    </td>

                    {/* Tokens */}
                    <td className="py-3 px-3 whitespace-nowrap">
                      <span className="font-mono text-foreground-muted">
                        {log.tokensUsed > 0 ? log.tokensUsed.toLocaleString() : '—'}
                      </span>
                    </td>

                    {/* API Key */}
                    <td className="py-3 px-3 whitespace-nowrap">
                      <button
                        onClick={() => handleCopy(`sk_••••${log.keyHint}`, log.id)}
                        className="inline-flex items-center gap-1 font-mono text-[11px] text-foreground hover:text-primary transition-colors"
                        title="Click to copy hint"
                      >
                        <Key className="size-3 text-foreground-muted" />
                        <span>••••{log.keyHint}</span>
                        {copiedId === log.id ? (
                          <Check className="size-3 text-emerald-500 ml-0.5" />
                        ) : (
                          <Copy className="size-3 text-foreground-muted opacity-0 group-hover:opacity-100 transition-opacity ml-0.5" />
                        )}
                      </button>
                    </td>

                    {/* Client IP */}
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span className="font-mono text-[11px] text-foreground-muted">
                        {log.ipAddress}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
